import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { CheckCircle2, Loader2, AlertTriangle, UserPlus, Copy, Check, LogIn, User, UserCheck } from 'lucide-react';
import { validateNIF, validatePhone } from '../utils/ptValidators';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO_URL = "https://customer-assets.emergentagent.com/job_utility-finder-7/artifacts/wusjk2bl_1.png";
const STORAGE_KEY = 'adiraqui_angariador_email';

const SERVICE_OPTIONS = [
  "Luz e Gas",
  "Solar",
  "Carregamento Eletrico",
  "Telecomunicacoes",
  "Alarmes",
  "Outro"
];

export default function LeadPublicForm() {
  const { referralCode } = useParams();
  const [referralInfo, setReferralInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [credentials, setCredentials] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  // Angariador flow states
  const [angariadorStep, setAngariadorStep] = useState('checking'); // checking, choose, login, register, ready
  const [angariadorInfo, setAngariadorInfo] = useState(null); // { name, email, id } if recognized
  const [loginEmail, setLoginEmail] = useState('');
  const [loginError, setLoginError] = useState('');
  const [checkingEmail, setCheckingEmail] = useState(false);

  const [form, setForm] = useState({
    angariador_nome: '',
    angariador_email: '',
    angariador_telefone: '',
    cliente_nome: '',
    cliente_telefone: '',
    cliente_email: '',
    cliente_nif: '',
    cliente_morada: '',
    cliente_localidade: '',
    servico_pretendido: '',
    observacoes: '',
  });

  useEffect(() => {
    const fetchReferralInfo = async () => {
      try {
        const res = await axios.get(`${API_URL}/leads/referral/${referralCode}`);
        setReferralInfo(res.data);
      } catch (err) {
        setError('Link invalido ou expirado.');
      } finally {
        setLoading(false);
      }
    };
    fetchReferralInfo();
  }, [referralCode]);

  // Check localStorage for returning angariador
  useEffect(() => {
    if (!loading && referralInfo) {
      const savedEmail = localStorage.getItem(STORAGE_KEY);
      if (savedEmail) {
        verifyAngariador(savedEmail, true);
      } else {
        setAngariadorStep('choose');
      }
    }
  }, [loading, referralInfo]);

  const verifyAngariador = async (email, fromStorage = false) => {
    setCheckingEmail(true);
    setLoginError('');
    try {
      const res = await axios.post(`${API_URL}/leads/check-angariador`, { email });
      if (res.data.found && !res.data.error) {
        setAngariadorInfo(res.data);
        setForm(prev => ({
          ...prev,
          angariador_nome: res.data.name,
          angariador_email: res.data.email,
        }));
        localStorage.setItem(STORAGE_KEY, email);
        setAngariadorStep('ready');
      } else if (res.data.error) {
        setLoginError(res.data.error);
        if (fromStorage) {
          localStorage.removeItem(STORAGE_KEY);
          setAngariadorStep('choose');
        }
      } else {
        if (fromStorage) {
          localStorage.removeItem(STORAGE_KEY);
          setAngariadorStep('choose');
        } else {
          setLoginError('Email nao encontrado. Ainda nao esta registado como angariador.');
        }
      }
    } catch (err) {
      if (fromStorage) {
        setAngariadorStep('choose');
      } else {
        setLoginError('Erro ao verificar email.');
      }
    } finally {
      setCheckingEmail(false);
    }
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.cliente_nome || !form.cliente_telefone) {
      setError('Nome e telefone do cliente sao obrigatorios.');
      return;
    }
    // Strict PT validation
    const nifCheck = validateNIF(form.cliente_nif);
    if (!nifCheck.ok) { setError(`NIF: ${nifCheck.error}`); return; }
    const phCheck = validatePhone(form.cliente_telefone, 'telemovel');
    if (!phCheck.ok) { setError(`Telefone: ${phCheck.error}`); return; }
    if (form.angariador_telefone) {
      const aphCheck = validatePhone(form.angariador_telefone, 'telemovel');
      if (!aphCheck.ok) { setError(`Telefone angariador: ${aphCheck.error}`); return; }
    }
    if (!form.servico_pretendido) { setError('Serviço pretendido é obrigatório.'); return; }
    setSubmitting(true);
    setError(null);
    try {
      const payload = Object.fromEntries(
        Object.entries(form).filter(([_, v]) => v !== '' && v !== null && v !== undefined)
      );
      const res = await axios.post(`${API_URL}/leads/public/${referralCode}`, payload);
      setSubmitted(true);
      if (res.data.angariador_account) {
        setCredentials(res.data.angariador_account);
      }
      // Save email for next time
      if (form.angariador_email) {
        localStorage.setItem(STORAGE_KEY, form.angariador_email);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao submeter lead.');
    } finally {
      setSubmitting(false);
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const switchToRegister = () => {
    setForm(prev => ({ ...prev, angariador_email: loginEmail }));
    setLoginError('');
    setAngariadorStep('register');
  };

  const changeAngariador = () => {
    localStorage.removeItem(STORAGE_KEY);
    setAngariadorInfo(null);
    setAngariadorStep('choose');
    setForm(prev => ({ ...prev, angariador_nome: '', angariador_email: '', angariador_telefone: '' }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#2D3E6F] via-[#1a2a4a] to-[#2D3E6F] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  if (!referralInfo && error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#2D3E6F] via-[#1a2a4a] to-[#2D3E6F] flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-2xl border-0">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-800 mb-2">Link Invalido</h2>
            <p className="text-slate-500">Este link de referencia nao e valido ou ja expirou.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#2D3E6F] via-[#1a2a4a] to-[#2D3E6F] flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-2xl border-0" data-testid="lead-success-card">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-2">Lead Submetida!</h2>
            <p className="text-slate-500 mb-6">
              A sua lead foi enviada com sucesso para o comercial <strong>{referralInfo?.comercial_name}</strong>.
            </p>
            
            {credentials && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 text-left mt-4" data-testid="angariador-credentials">
                <div className="flex items-center gap-2 mb-3">
                  <UserPlus className="w-5 h-5 text-blue-600" />
                  <h3 className="font-semibold text-blue-800">A sua conta foi criada!</h3>
                </div>
                <p className="text-sm text-blue-700 mb-1">
                  Guarde estas credenciais para acompanhar o estado das suas leads:
                </p>
                <p className="text-xs text-blue-500 mb-4">
                  Tambem enviamos estes dados para o seu email.
                </p>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between bg-white rounded-lg p-3 border border-blue-100">
                    <div>
                      <span className="text-xs text-slate-500 block">Email</span>
                      <span className="font-mono text-sm font-medium" data-testid="cred-email">{credentials.email}</span>
                    </div>
                    <button onClick={() => copyToClipboard(credentials.email, 'email')} className="text-blue-500 hover:text-blue-700 p-1" data-testid="copy-email-btn">
                      {copiedField === 'email' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                  <div className="flex items-center justify-between bg-white rounded-lg p-3 border border-blue-100">
                    <div>
                      <span className="text-xs text-slate-500 block">Password</span>
                      <span className="font-mono text-sm font-medium" data-testid="cred-password">{credentials.password}</span>
                    </div>
                    <button onClick={() => copyToClipboard(credentials.password, 'password')} className="text-blue-500 hover:text-blue-700 p-1" data-testid="copy-password-btn">
                      {copiedField === 'password' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                <a href="/login" className="mt-4 inline-flex items-center justify-center w-full gap-2 bg-[#2D3E6F] text-white text-sm font-medium py-2.5 px-4 rounded-lg hover:bg-[#1a2a4a] transition-colors" data-testid="access-crm-btn">
                  <LogIn className="w-4 h-4" />
                  Aceder ao CRM
                </a>
              </div>
            )}
            
            <Button
              className="mt-6 w-full bg-[#2D3E6F] hover:bg-[#1a2a4a]"
              onClick={() => { setSubmitted(false); setCredentials(null); }}
              data-testid="submit-another-btn"
            >
              Submeter outra Lead
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#2D3E6F] via-[#1a2a4a] to-[#2D3E6F] py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Logo + Header */}
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="Adiraqui" className="h-20 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white">Submeter Lead</h1>
          {referralInfo && (
            <p className="text-slate-300 mt-1">
              Comercial: <strong className="text-white">{referralInfo.comercial_name}</strong>
              {referralInfo.partner_name && (
                <span className="text-slate-400"> | {referralInfo.partner_name}</span>
              )}
            </p>
          )}
        </div>

        {/* Step: Choose - "Ja e angariador?" */}
        {angariadorStep === 'choose' && (
          <Card className="shadow-2xl border-0 mb-6" data-testid="angariador-choice-card">
            <CardContent className="p-8">
              <h2 className="text-lg font-bold text-slate-800 text-center mb-6">Ja e angariador Adiraqui?</h2>
              <div className="grid grid-cols-2 gap-4">
                <Button
                  variant="outline"
                  className="h-24 flex flex-col gap-2 border-2 hover:border-[#2D3E6F] hover:bg-blue-50 transition-all"
                  onClick={() => setAngariadorStep('login')}
                  data-testid="btn-ja-angariador"
                >
                  <UserCheck className="w-6 h-6 text-[#2D3E6F]" />
                  <span className="font-semibold text-slate-700">Sim, ja sou</span>
                </Button>
                <Button
                  variant="outline"
                  className="h-24 flex flex-col gap-2 border-2 hover:border-emerald-500 hover:bg-emerald-50 transition-all"
                  onClick={() => setAngariadorStep('register')}
                  data-testid="btn-novo-angariador"
                >
                  <UserPlus className="w-6 h-6 text-emerald-600" />
                  <span className="font-semibold text-slate-700">Nao, sou novo</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step: Login - verify email */}
        {angariadorStep === 'login' && (
          <Card className="shadow-2xl border-0 mb-6" data-testid="angariador-login-card">
            <CardContent className="p-8">
              <h2 className="text-lg font-bold text-slate-800 mb-4">Introduza o seu email</h2>
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-600">Email registado</Label>
                  <Input
                    type="email"
                    value={loginEmail}
                    onChange={(e) => { setLoginEmail(e.target.value); setLoginError(''); }}
                    placeholder="seu@email.com"
                    data-testid="login-email-input"
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); verifyAngariador(loginEmail); } }}
                  />
                </div>
                {loginError && (
                  <div className="bg-amber-50 text-amber-800 px-4 py-3 rounded-lg text-sm" data-testid="login-error">
                    <p className="font-medium">{loginError}</p>
                    {loginError.includes('nao encontrado') && (
                      <Button variant="link" className="text-[#2D3E6F] p-0 h-auto mt-1 text-sm" onClick={switchToRegister} data-testid="btn-switch-register">
                        Registar como novo angariador
                      </Button>
                    )}
                  </div>
                )}
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => { setAngariadorStep('choose'); setLoginEmail(''); setLoginError(''); }}>Voltar</Button>
                  <Button
                    className="flex-1 bg-[#2D3E6F] hover:bg-[#1a2a4a]"
                    onClick={() => verifyAngariador(loginEmail)}
                    disabled={!loginEmail || checkingEmail}
                    data-testid="btn-verify-email"
                  >
                    {checkingEmail ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Verificar
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step: Checking (from localStorage) */}
        {angariadorStep === 'checking' && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-white mr-3" />
            <span className="text-white">A verificar...</span>
          </div>
        )}

        {/* Main form - visible when ready or register */}
        {(angariadorStep === 'ready' || angariadorStep === 'register') && (
          <Card className="shadow-2xl border-0" data-testid="lead-form-card">
            <CardHeader className="border-b bg-slate-50/80 rounded-t-xl">
              <CardTitle className="text-lg text-slate-700">Dados da Lead</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Angariador Section */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between pb-2 border-b">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-[#2D3E6F]" />
                      <h3 className="font-semibold text-slate-700 text-sm">Os seus dados (angariador)</h3>
                    </div>
                    {angariadorStep === 'ready' && (
                      <button type="button" onClick={changeAngariador} className="text-xs text-slate-400 hover:text-[#2D3E6F] transition-colors" data-testid="btn-change-angariador">
                        Alterar
                      </button>
                    )}
                  </div>

                  {angariadorStep === 'ready' && angariadorInfo && (
                    <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-lg p-3" data-testid="angariador-recognized">
                      <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                        <UserCheck className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-emerald-800 text-sm">{angariadorInfo.name}</p>
                        <p className="text-emerald-600 text-xs">{angariadorInfo.email}</p>
                      </div>
                    </div>
                  )}

                  {angariadorStep === 'register' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-600">Nome</Label>
                        <Input
                          value={form.angariador_nome}
                          onChange={(e) => handleChange('angariador_nome', e.target.value)}
                          placeholder="O seu nome"
                          data-testid="angariador-nome-input"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-600">Email</Label>
                        <Input
                          type="email"
                          value={form.angariador_email}
                          onChange={(e) => handleChange('angariador_email', e.target.value)}
                          placeholder="seu@email.com"
                          data-testid="angariador-email-input"
                        />
                        <p className="text-xs text-slate-400 mt-1">Sera criada uma conta para acompanhar as leads</p>
                      </div>
                      <div>
                        <Label className="text-slate-600">Telefone</Label>
                        <Input
                          value={form.angariador_telefone}
                          onChange={(e) => handleChange('angariador_telefone', e.target.value)}
                          placeholder="912345678"
                          data-testid="angariador-telefone-input"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Client Section */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 pb-2 border-b">
                    <UserPlus className="w-4 h-4 text-emerald-600" />
                    <h3 className="font-semibold text-slate-700 text-sm">Dados do Cliente</h3>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-600">Nome do Cliente *</Label>
                      <Input value={form.cliente_nome} onChange={(e) => handleChange('cliente_nome', e.target.value)} placeholder="Nome completo" required data-testid="cliente-nome-input" />
                    </div>
                    <div>
                      <Label className="text-slate-600">Telefone do Cliente *</Label>
                      <Input value={form.cliente_telefone} onChange={(e) => handleChange('cliente_telefone', e.target.value)} placeholder="912345678" required data-testid="cliente-telefone-input" maxLength={9} />
                      {form.cliente_telefone && !validatePhone(form.cliente_telefone, 'telemovel').ok && (
                        <p className="text-xs text-rose-600 mt-1">{validatePhone(form.cliente_telefone, 'telemovel').error}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-slate-600">Email do Cliente</Label>
                      <Input type="email" value={form.cliente_email} onChange={(e) => handleChange('cliente_email', e.target.value)} placeholder="cliente@email.com" data-testid="cliente-email-input" />
                    </div>
                    <div>
                      <Label className="text-slate-600">NIF</Label>
                      <Input value={form.cliente_nif} onChange={(e) => handleChange('cliente_nif', e.target.value)} placeholder="123456789" maxLength={9} data-testid="cliente-nif-input" />
                      {form.cliente_nif && !validateNIF(form.cliente_nif).ok && (
                        <p className="text-xs text-rose-600 mt-1">{validateNIF(form.cliente_nif).error}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-slate-600">Localidade</Label>
                      <Input value={form.cliente_localidade} onChange={(e) => handleChange('cliente_localidade', e.target.value)} placeholder="Lisboa, Porto..." data-testid="cliente-localidade-input" />
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-slate-600">Morada</Label>
                      <Input value={form.cliente_morada} onChange={(e) => handleChange('cliente_morada', e.target.value)} placeholder="Morada completa" data-testid="cliente-morada-input" />
                    </div>
                  </div>
                </div>

                {/* Service */}
                <div className="space-y-4">
                  <div>
                    <Label className="text-slate-600">Servico Pretendido <span className="text-rose-500">*</span></Label>
                    <Select value={form.servico_pretendido} onValueChange={(v) => handleChange('servico_pretendido', v)}>
                      <SelectTrigger data-testid="servico-select" className={!form.servico_pretendido ? 'border-rose-300' : ''}><SelectValue placeholder="Selecionar servico..." /></SelectTrigger>
                      <SelectContent>
                        {SERVICE_OPTIONS.map(s => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-600">Observacoes</Label>
                    <Textarea value={form.observacoes} onChange={(e) => handleChange('observacoes', e.target.value)} placeholder="Notas adicionais..." rows={3} data-testid="observacoes-input" />
                  </div>
                </div>

                {error && (
                  <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2" data-testid="form-error">
                    <AlertTriangle className="w-4 h-4" />
                    {error}
                  </div>
                )}

                <Button type="submit" disabled={submitting} className="w-full bg-[#2D3E6F] hover:bg-[#1a2a4a] py-6 text-base" data-testid="submit-lead-btn">
                  {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />A submeter...</>) : 'Submeter Lead'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
